import React from 'react';
import { NavLink } from 'react-router-dom';
import '../styles/business.css';

const VIDEO_PACKAGES = [
  { label: 'Starter', impressions: 5000, fifteen: 100, thirty: 150 },
  { label: 'Standard', impressions: 10000, fifteen: 200, thirty: 300 },
  { label: 'Growth', impressions: 25000, fifteen: 475, thirty: 700 },
  { label: 'Reach', impressions: 50000, fifteen: 900, thirty: 1300 },
];

const DISPLAY_PACKAGES = [
  { key: 'starter', label: 'Starter', impressions: 10000, price: 25, cpm: 2.50 },
  { key: 'standard', label: 'Standard', impressions: 25000, price: 60, cpm: 2.40 },
  { key: 'growth', label: 'Growth', impressions: 50000, price: 110, cpm: 2.20 },
  { key: 'reach', label: 'Reach', impressions: 100000, price: 200, cpm: 2.00 },
];

function formatInt(value) {
  return Number(value || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function money(value) {
  const number = Number(value || 0);
  return number.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: Number.isInteger(number) ? 0 : 2 });
}

function AdvertisingNav() {
  return (
    <nav className="business-subnav" aria-label="WatchVIM advertising">
      <NavLink to="/advertise">Overview</NavLink>
      <NavLink to="/advertise/video">Video Ads</NavLink>
      <NavLink to="/advertise/display">Display Ads</NavLink>
      <NavLink to="/advertise/pricing">Pricing</NavLink>
      <NavLink to="/advertise/dashboard">Campaign Report</NavLink>
      <NavLink to="/advertise/sponsorships">Sponsorships</NavLink>
    </nav>
  );
}

function PageShell({ title, subtitle, children }) {
  return (
    <section className="business-page advertise-page">
      <header className="business-hero">
        <span className="eyebrow">WatchVIM for Brands</span>
        <h1>{title}</h1>
        <p>{subtitle}</p>
      </header>
      <div className="business-content"><AdvertisingNav />{children}</div>
    </section>
  );
}

export function AdvertisingOverviewPage() {
  return (
    <PageShell title="Put Your Brand in Front of WatchVIM Viewers" subtitle="Choose measurable video or display-image advertising with guaranteed impression goals, secure PayPal checkout, and private campaign reporting.">
      <div className="business-card-grid three">
        <article className="business-card">
          <span className="business-kicker">Starting at $100</span>
          <h2>Video Advertising</h2>
          <p>Run 15- or 30-second pre-roll and mid-roll campaigns. A 15-second Starter campaign includes 5,000 guaranteed impressions.</p>
          <NavLink className="business-primary" to="/advertise/video?duration=15&package=starter">Build Video Campaign</NavLink>
        </article>
        <article className="business-card">
          <span className="business-kicker">Starting at $25</span>
          <h2>Display Image Advertising</h2>
          <p>Launch an approved image campaign with 10,000 guaranteed impressions and track clicks and CTR from your campaign dashboard.</p>
          <NavLink className="business-primary" to="/advertise/display?package=starter">Start a $25 Campaign</NavLink>
        </article>
        <article className="business-card">
          <span className="business-kicker">Private reporting</span>
          <h2>Measure Delivery</h2>
          <p>Track purchased, delivered, and remaining impressions. Video campaigns add completion metrics; display campaigns add click-through reporting.</p>
          <NavLink to="/advertise/dashboard">Open Campaign Report</NavLink>
        </article>
      </div>
      <div className="business-card-grid two">
        <article className="business-card">
          <h2>Target the platform or a title</h2>
          <p>Self-service campaigns can run across eligible WatchVIM inventory or target a specific published title. Placement remains subject to creative approval and available inventory.</p>
        </article>
        <article className="business-card">
          <h2>Need something bigger?</h2>
          <p>Premiere sponsorships, branded collections, title integrations, and larger cross-platform packages are quoted individually.</p>
          <NavLink to="/advertise/sponsorships">Explore Sponsorships</NavLink>
        </article>
      </div>
    </PageShell>
  );
}

export function AdvertisingPricingPage() {
  return (
    <PageShell title="Advertising Pricing" subtitle="Choose a guaranteed impression package. Video campaigns can also use a daily budget; display-image campaigns start at just $25.">
      <div className="business-card">
        <span className="business-kicker">Video ads</span>
        <h2>15-second campaigns start at $100</h2>
        <p>15-second video uses a $20 base CPM and 30-second video uses a $30 base CPM. Choose pre-roll, mid-roll, or both.</p>
      </div>
      <div className="business-card-grid four">
        {VIDEO_PACKAGES.map((pkg) => (
          <article className="business-card pricing-card" key={pkg.label}>
            <span className="business-kicker">{pkg.label}</span>
            <h2>{formatInt(pkg.impressions)} impressions</h2>
            <p><strong>15 seconds:</strong> {money(pkg.fifteen)}</p>
            <p><strong>30 seconds:</strong> {money(pkg.thirty)}</p>
            <NavLink className="business-primary" to={`/advertise/video?package=${pkg.label.toLowerCase()}&duration=15`}>Build Video Campaign</NavLink>
          </article>
        ))}
      </div>
      <div className="business-card">
        <span className="business-kicker">Display image ads</span>
        <h2>Start at $25 for 10,000 guaranteed impressions</h2>
        <p>Display-image campaigns are sold on guaranteed impression goals and include click and CTR reporting. The Starter package begins at a $2.50 effective CPM.</p>
      </div>
      <div className="business-card-grid four">
        {DISPLAY_PACKAGES.map((pkg) => (
          <article className="business-card pricing-card" key={pkg.key}>
            <span className="business-kicker">{pkg.label}</span>
            <h2>{money(pkg.price)}</h2>
            <p><strong>{formatInt(pkg.impressions)}</strong> guaranteed impressions</p>
            <p>{money(pkg.cpm)} effective CPM</p>
            <NavLink className="business-primary" to={`/advertise/display?package=${pkg.key}`}>Build Display Campaign</NavLink>
          </article>
        ))}
      </div>
      <div className="business-card-grid two">
        <article className="business-card">
          <span className="business-kicker">Flexible video pacing</span>
          <h2>Daily Budget Campaigns</h2>
          <p>Video advertisers can select a 1–30 day flight and daily spend. WatchVIM calculates the daily impression cap from the applicable video CPM.</p>
          <NavLink to="/advertise/video?mode=daily&duration=15">Set a Video Daily Budget</NavLink>
        </article>
        <article className="business-card">
          <span className="business-kicker">How billing works</span>
          <h2>Pay once. Track delivery.</h2>
          <p>PayPal payment is verified server-side. After WatchVIM approves the creative, impressions begin counting toward the purchased goal and stop automatically when the goal is fulfilled.</p>
        </article>
      </div>
    </PageShell>
  );
}
